import { useState, useCallback, useMemo } from "react";
import { Vehicle } from "types";

export type VehicleFilters = {
  minCapacity: number | null;
};

type UseVehicleFiltersProps = {
  defaultMinCapacity: number;
};

function useVehicleFilters(props: UseVehicleFiltersProps) {
  const { defaultMinCapacity } = props;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<VehicleFilters>({
    minCapacity: defaultMinCapacity,
  });

  const handleFilterOpen = useCallback(() => {
    setIsFilterOpen(true);
  }, []);

  const handleFilterClose = useCallback(() => {
    setIsFilterOpen(false);
  }, []);

  const updateFilter = useCallback(
    (key: keyof VehicleFilters, value: number | null) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const clearFilter = useCallback((key: keyof VehicleFilters) => {
    setFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
  }, []);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(value => value !== null).length;
  }, [filters]);

  // derived data
  const hasActiveFilters = activeFilterCount > 0;

  const filterVehicles = useCallback(
    (vehicles: Vehicle[]) => {
      // Safety check for undefined or null vehicles
      if (!vehicles || !Array.isArray(vehicles)) {
        return [];
      }
      
      return vehicles.filter((vehicle) => {
        if (filters.minCapacity !== null && vehicle.capacity < filters.minCapacity) {
          return false;
        }
        return true;
      });
    },
    [filters]
  );

  return {
    isFilterOpen,
    filters,
    hasActiveFilters,
    activeFilterCount,
    handleFilterOpen,
    handleFilterClose,
    updateFilter,
    clearFilter,
    filterVehicles,
  };
}

export default useVehicleFilters;